import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor, type PermissionState } from "@capacitor/core";
import { LocalNotifications } from "@capacitor/local-notifications";
import {
  PushNotifications,
  type Token,
} from "@capacitor/push-notifications";
import { SplashScreen } from "@capacitor/splash-screen";
import { StatusBar, Style } from "@capacitor/status-bar";
import { api, json } from "./api";
import { messageNotificationCopy } from "./notification-copy";
import type { Locale, RealtimeMessage } from "./types";

const LOCAL_MESSAGE_NOTIFICATION_ID = 1001;
const LOCAL_PERMISSION_CONFIRMATION_ID = 1000;
const LOCAL_MESSAGE_CHANNEL_ID = "local-messages";
const NOTIFICATION_PERMISSION_PROMPT_KEY =
  "nearby-heart-notification-permission-prompt-v2";
const LATEST_NOTIFICATION_CONVERSATION_KEY =
  "nearby-heart-latest-notification-conversation";

export type NativePushState = {
  native: boolean;
  permission: PermissionState | "unavailable";
  registered: boolean;
  serverConfigured: boolean;
  error?: string;
};

let initializationPromise: Promise<void> | undefined;
let nativeAppActive = true;
let registrationWaiter:
  | {
      resolve: (state: NativePushState) => void;
      reject: (error: Error) => void;
      locale: Locale;
    }
  | undefined;

export function isNativeApp(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android";
}

function currentLocale(): Locale {
  return window.location.pathname.startsWith("/dating-v1/vn/") ? "vn" : "en";
}

function openConversation(data: Record<string, unknown>): void {
  const conversationId = String(data.conversationId ?? "");
  if (!/^[0-9a-f-]{36}$/i.test(conversationId)) return;
  const locale = data.locale === "zh" || data.locale === "vn" ? data.locale : currentLocale();
  window.location.assign(`/dating-v1/${locale}/chat/${conversationId}`);
}

export async function showNativeMessageNotification(
  message: RealtimeMessage,
  locale: Locale,
  senderName?: string,
): Promise<void> {
  if (!isNativeApp()) return;
  await initializeNativeShell();
  if (nativeAppActive) return;
  const permission = await LocalNotifications.checkPermissions();
  if (permission.display !== "granted") return;
  const copy = messageNotificationCopy(message, locale, senderName);
  localStorage.setItem(
    LATEST_NOTIFICATION_CONVERSATION_KEY,
    message.conversation_id,
  );
  await LocalNotifications.schedule({
    notifications: [
      {
        id: LOCAL_MESSAGE_NOTIFICATION_ID,
        title: copy.title,
        body: copy.body,
        channelId: LOCAL_MESSAGE_CHANNEL_ID,
        badge: 1,
        foreground: true,
        autoCancel: true,
        ongoing: false,
        extra: { conversationId: message.conversation_id, locale },
      },
    ],
  });
}

export async function clearNativeMessageNotification(
  conversationId?: string,
): Promise<void> {
  if (!isNativeApp()) return;
  const latest = localStorage.getItem(LATEST_NOTIFICATION_CONVERSATION_KEY);
  if (conversationId && latest && latest !== conversationId) return;
  await Promise.allSettled([
    LocalNotifications.cancel({
      notifications: [{ id: LOCAL_MESSAGE_NOTIFICATION_ID }],
    }),
    LocalNotifications.removeDeliveredNotificationsById({
      ids: [LOCAL_MESSAGE_NOTIFICATION_ID],
    }),
  ]);
  localStorage.removeItem(LATEST_NOTIFICATION_CONVERSATION_KEY);
}

async function saveRegistration(token: Token, locale: Locale) {
  return api<{ registered: boolean; serverConfigured: boolean }>(
    "/devices/push",
    json("POST", { token: token.value, platform: "android", locale }),
  );
}

export function initializeNativeShell(): Promise<void> {
  if (!isNativeApp()) return Promise.resolve();
  if (initializationPromise) return initializationPromise;
  initializationPromise = initializeNativeShellOnce();
  return initializationPromise;
}

async function initializeNativeShellOnce(): Promise<void> {
  await Promise.allSettled([
    StatusBar.setOverlaysWebView({ overlay: false }),
    StatusBar.setStyle({ style: Style.Light }),
    StatusBar.setBackgroundColor({ color: "#fffaf6" }),
    PushNotifications.createChannel({
      id: "messages",
      name: "Messages",
      description: "New messages and greetings",
      importance: 4,
      visibility: 1,
      vibration: true,
    }),
    LocalNotifications.createChannel({
      id: LOCAL_MESSAGE_CHANNEL_ID,
      name: "Messages while app is running",
      description: "New messages received while Nearby Heart is in the background",
      importance: 4,
      visibility: 1,
      vibration: true,
      lights: true,
      lightColor: "#ff4f3e",
    }),
  ]);

  await PushNotifications.addListener("registration", async (token) => {
    const waiter = registrationWaiter;
    const locale = waiter?.locale ?? currentLocale();
    try {
      const result = await saveRegistration(token, locale);
      waiter?.resolve({
        native: true,
        permission: "granted",
        registered: result.registered,
        serverConfigured: result.serverConfigured,
      });
    } catch (error) {
      waiter?.reject(error as Error);
    } finally {
      registrationWaiter = undefined;
    }
  });
  await PushNotifications.addListener("registrationError", (error) => {
    registrationWaiter?.reject(
      new Error(error.error || "Push registration failed"),
    );
    registrationWaiter = undefined;
  });
  await PushNotifications.addListener(
    "pushNotificationActionPerformed",
    ({ notification }) => openConversation(notification.data),
  );
  await LocalNotifications.addListener(
    "localNotificationActionPerformed",
    ({ notification }) => {
      localStorage.removeItem(LATEST_NOTIFICATION_CONVERSATION_KEY);
      openConversation((notification.extra ?? {}) as Record<string, unknown>);
    },
  );

  await CapacitorApp.addListener("backButton", () => {
    const route = window.location.pathname;
    if (/\/(nearby|messages|likes|account|start)\/?$/.test(route)) {
      void CapacitorApp.exitApp();
      return;
    }
    window.history.back();
  });
  await CapacitorApp.addListener("appStateChange", ({ isActive }) => {
    nativeAppActive = isActive;
    if (isActive) {
      window.dispatchEvent(new Event("focus"));
    }
  });
  const appState = await CapacitorApp.getState();
  nativeAppActive = appState.isActive;
  await SplashScreen.hide();
}

export async function requestNativeNotificationPermissionOnce(): Promise<void> {
  if (!isNativeApp()) return;
  await initializeNativeShell();
  if (localStorage.getItem(NOTIFICATION_PERMISSION_PROMPT_KEY)) return;
  localStorage.setItem(NOTIFICATION_PERMISSION_PROMPT_KEY, "requested");
  let permission = await LocalNotifications.checkPermissions();
  if (
    permission.display === "prompt" ||
    permission.display === "prompt-with-rationale"
  ) {
    permission = await LocalNotifications.requestPermissions();
  }
  if (permission.display !== "granted") return;
  const locale = currentLocale();
  await LocalNotifications.schedule({
    notifications: [
      {
        id: LOCAL_PERMISSION_CONFIRMATION_ID,
        title: "Nearby Heart",
        body:
          locale === "zh"
            ? "消息通知已开启。新消息到达时会提醒你。"
            : "Notifications are on. We will alert you when a new message arrives.",
        channelId: LOCAL_MESSAGE_CHANNEL_ID,
        foreground: true,
        autoCancel: true,
        ongoing: false,
      },
    ],
  });
}

export async function getNativePushState(): Promise<NativePushState> {
  if (!isNativeApp())
    return {
      native: false,
      permission: "unavailable",
      registered: false,
      serverConfigured: false,
    };
  await initializeNativeShell();
  const [permission, status] = await Promise.all([
    LocalNotifications.checkPermissions(),
    api<{ registered: boolean; serverConfigured: boolean }>(
      "/devices/push/status",
    ),
  ]);
  return {
    native: true,
    permission: permission.display,
    registered: permission.display === "granted",
    serverConfigured: status.serverConfigured,
  };
}

export async function enableNativePush(
  locale: Locale,
): Promise<NativePushState> {
  if (!isNativeApp()) return getNativePushState();
  await initializeNativeShell();
  let permission = await LocalNotifications.checkPermissions();
  if (
    permission.display === "prompt" ||
    permission.display === "prompt-with-rationale"
  )
    permission = await LocalNotifications.requestPermissions();
  if (permission.display !== "granted")
    return {
      native: true,
      permission: permission.display,
      registered: false,
      serverConfigured: false,
    };

  const status = await api<{ registered: boolean; serverConfigured: boolean }>(
    "/devices/push/status",
  ).catch(() => ({ registered: false, serverConfigured: false }));
  if (!status.serverConfigured)
    return {
      native: true,
      permission: "granted",
      registered: true,
      serverConfigured: false,
    };

  return new Promise<NativePushState>((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      if (!registrationWaiter) return;
      registrationWaiter = undefined;
      reject(new Error("Push registration timed out"));
    }, 15_000);
    registrationWaiter = {
      locale,
      resolve: (state) => {
        window.clearTimeout(timeout);
        resolve(state);
      },
      reject: (error) => {
        window.clearTimeout(timeout);
        reject(error);
      },
    };
    void PushNotifications.register().catch(registrationWaiter.reject);
  });
}
